import React, { useEffect, useMemo } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import jwtDecode from 'jwt-decode';

import EventBus, { AuthEvent } from './AuthEventBus';
import { InvalidUrlToken } from './InvalidUrlToken';

export enum TokenType {
  cognito_token = 'cognito-token',
  url_token_invalid = 'url-token-invalid',
  url_token_too_old = 'url-token-too-old',
  url_token_valid = 'url-token-valid',
  no_token = 'no-token',
}

export type AuthState =
  | {
      type: TokenType.cognito_token;
      token: string;
    }
  | {
      type: TokenType.url_token_invalid;
      token: string;
    }
  | {
      type: TokenType.url_token_too_old;
      token: string;
    }
  | {
      type: TokenType.url_token_valid;
      token: string;
    }
  | {
      type: TokenType.no_token;
      token: undefined;
    };

interface CarbonCropJWT {
  exp?: number;
}

export const getUrlToken = (location: { search: string }): string | undefined => {
  const queryData = queryString.parse(location.search);
  if (typeof queryData.token === 'string') {
    return queryData.token;
  }
  return undefined;
};

/**
 * Does some rough checks of the validity of the token. This is not a security check, just a sanity check.
 */
const getUrlTokenState = (urlToken: string): AuthState => {
  try {
    const token = jwtDecode(urlToken) as CarbonCropJWT;
    if (!token.exp) {
      return {
        type: TokenType.url_token_invalid,
        token: urlToken,
      };
    }
    if (token.exp < Date.now() / 1000) {
      return {
        type: TokenType.url_token_too_old,
        token: urlToken,
      };
    }
    return {
      type: TokenType.url_token_valid,
      token: urlToken,
    };
  } catch (e) {
    return {
      type: TokenType.url_token_invalid,
      token: urlToken,
    };
  }
};

/**
 * Pick which token to use. If there is a cognito token, use that. If there is a url token, use that. Otherwise, there is no token.
 */
const getAuthState = (
  cognitoToken: string | undefined,
  urlToken: string | undefined,
): AuthState => {
  if (urlToken) {
    return getUrlTokenState(urlToken);
  }
  if (cognitoToken) {
    return {
      type: TokenType.cognito_token,
      token: cognitoToken,
    };
  }
  return {
    type: TokenType.no_token,
    token: undefined,
  };
};

/**
 * There are multiple means of logging in to our systems, and checking if you are logged in involves
 * checking these different methods. This hook condenses all the authentication logic down into a single
 * enum-like object.
 *
 * @returns The current state of login to our systems
 */
export const useAuthState = (): AuthState => {
  const { user } = useAuthenticator((context) => [context.user]);
  const loc = useLocation();

  const tokenFromCognito = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (user as any)?.signInUserSession?.idToken.jwtToken,
    [user],
  );

  const urlToken = useMemo(() => getUrlToken(loc), [loc]);

  const authState = useMemo(
    () => getAuthState(tokenFromCognito, urlToken),
    [tokenFromCognito, urlToken],
  );

  return authState;
};

/**
 * The ProtectedRoute is a visual component that wraps other components and only renders them if the user
 * is logged in. If the user is not logged in, it will redirect them to the login page.
 *
 * It is important to realize that this is a frontend-only component. Don't rely on hiding things behind
 * the protected route to prevent unauthorized access. The backend must be where our security is actually
 * implemented.
 */
const ProtectedRoute: React.FC = ({ children }) => {
  const navigate = useNavigate();
  const authState = useAuthState();

  // Attach listener for AuthEvent
  useEffect(() => {
    EventBus.on(AuthEvent.logout, () => {
      window.onbeforeunload = null;
      window.location.replace('/login');
    });

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      EventBus.remove(AuthEvent.logout, () => {});
    };
  }, []);

  useEffect(() => {
    if (authState.type === TokenType.no_token) {
      window.sessionStorage.setItem(
        'pre_login_page_url',
        window.location.pathname + window.location.search + window.location.hash,
      );
      navigate('/login');
    }
  }, [authState, navigate]);

  // Tell the user why they aren't logged in, or render the children
  switch (authState.type) {
    case TokenType.no_token:
      return <div>Redirecting to Login...</div>;
    case TokenType.url_token_invalid:
      return <InvalidUrlToken state="invalid" />;
    case TokenType.url_token_too_old:
      return <InvalidUrlToken state="too-old" />;
    case TokenType.url_token_valid:
      return <>{children}</>;
    case TokenType.cognito_token:
      return <>{children}</>;
    default:
      return <div>Unknown Auth State</div>;
  }
};
export default ProtectedRoute;
